import Annotation from "./annotation";
import { Directions } from "../../config/config";
import { getArcPosition } from "../geometry";

class RadialCirclesPattern extends Annotation {
  constructor() {
    super();

    this.color = "rgba(0,176,240, 0.5)";
    this.colorActive = "#00b0f0";
    this.lineWidth = 1;
    this.canvasSize = null;
    this.sliceCurrentWidth = 3;
    this.boxWidth = 3;
    
    this.slices = [];
    this.sliceCurrent = null;
    this.sliceBounds = null;
    this.slicesVertical = false;
    this.radialSlices = [];
    
    this.circleSlices = [];
    this.circleRadius = 0;

    this.triangleLength = 8;
    this.triangleInternalAngle = Math.PI / 8;
    
    this.minX = null;
    this.maxX = null;
    this.minY = null;
    this.maxY = null;

    this.drawAllSlices = true;
    this.showExtension = true;
    this.showBindingBox = false;

    this.canvasSize = null;
    this.drawBoundingBoxWidth = null;
    this.drawBoundingBoxHeight = null;

    this.isRadial = false;
    this.hasCircleScans = true;
    this.direction = "";
  }

  draw(ctx) {
    if (!this.canvasSize) 
      return;

    let w = this.canvasSize.w;
    let h = this.canvasSize.h;

    if (this.slices.length && this.sliceCurrent != null) {
      this.drawSliceCollection(ctx, this.radialSlices, this.sliceCurrent, w, h, this.color, this.colorActive);
      this.drawCircleCollection(ctx, this.circleSlices, this.sliceCurrent, w, h, this.color, this.colorActive);
      this.drawTriangle(ctx, this.slices[this.sliceCurrent], w, h, this.colorActive);
    }

    if (this.showBindingBox) this.drawBoundingBox(ctx, w, h);
  }

  drawSliceCollection(ctx, slices, current, width, height, color, colorActive) {
    ctx.lineWidth = this.lineWidth;
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    if (this.drawAllSlices) slices.forEach(slice => this.drawSlice(ctx, slice, width, height));
    ctx.strokeStyle = colorActive;
    ctx.fillStyle = colorActive;
    ctx.lineWidth = this.showExtension ? this.sliceCurrentWidth : this.boxWidth;
    let slice = slices.find(s => s.index === current);
    if (slice) this.drawSlice(ctx, slice, width, height);
  }

  drawSlice(ctx, slice, width, height) {
    ctx.beginPath();
    ctx.moveTo(slice.eX * width, slice.eY * height);
    ctx.lineTo(slice.sX * width, slice.sY * height);
    ctx.stroke();
    ctx.closePath();
  }

  drawCircleCollection(ctx, slices, current, width, height, color, colorActive) {
    ctx.lineWidth = this.lineWidth;
    ctx.strokeStyle = color;
    ctx.fillStyle = color;

    let centerX = width * slices[0].eX;
    let centerY = height * slices[0].eY;
    if (this.circleRadius == 0)
      this.circleRadius = ((width * Math.abs(slices[0].eX - slices[0].sX)) + (height * Math.abs(slices[0].eY - slices[0].sY))) / 2;

    this.drawCircle(ctx, centerX, centerY, width * (slices[0].eX - slices[0].sX));
    this.drawCircle(ctx, centerX, centerY, width * (slices[1].eX - slices[1].sX));
    this.drawCircle(ctx, centerX, centerY, width * (slices[2].eX - slices[2].sX));

    ctx.strokeStyle = colorActive;
    ctx.fillStyle = colorActive;
    ctx.lineWidth = this.showExtension ? this.sliceCurrentWidth : this.boxWidth;
    let slice = slices.find(s => s.index === current);
    if (slice) this.drawCircle(ctx, centerX, centerY, width * (slice.eX - slice.sX));
  }

  drawCircle(ctx, centerX, centerY, radius) {
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.stroke();
    ctx.closePath();
  }

  drawTriangle(ctx, currentSlice, x, y, color) {
    let angle, xPos, yPos;
    if (currentSlice.index < this.slices.length - 3) {
      if (this.direction == "CW") {
        xPos = currentSlice.eX * x;
        angle = Math.atan2(currentSlice.eY - currentSlice.sY, currentSlice.eX - currentSlice.sX);
        yPos = currentSlice.eY * y;
      }
      else {
        angle = Math.atan2(currentSlice.sY - currentSlice.eY, currentSlice.sX - currentSlice.eX);
        xPos = currentSlice.sX * x;
        yPos = currentSlice.sY * y;
      }
      let { x: x1, y: y1 } = this.getTriangleCoordinates(xPos, yPos, angle + this.triangleInternalAngle);
      let { x: x2, y: y2 } = this.getTriangleCoordinates(xPos, yPos, angle - this.triangleInternalAngle);
  
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(xPos, yPos);
      ctx.lineTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.lineTo(xPos, yPos);
      ctx.stroke();
      ctx.fill();
      ctx.closePath();
    }
  }

  getTriangleCoordinates(x, y, angle) {
    return {
      x: x - this.triangleLength * Math.cos(angle),
      y: y - this.triangleLength * Math.sin(angle)
    };
  }

  drawBoundingBox(ctx, w, h) {
    this.drawBoundingBoxWidth = Math.abs(this.maxX - this.minX) * w;
    this.drawBoundingBoxHeight = Math.abs(this.minY - this.maxY) * h;
    ctx.lineWidth = this.boxWidth;
    ctx.strokeStyle = this.compositeColorActive;
    ctx.fillStyle = this.compositeColorActive;
    ctx.strokeRect(this.minX * w, this.minY * h, this.drawBoundingBoxWidth, this.drawBoundingBoxHeight);
  }

  updateSlices(slices) {
    let { processed, vertical } = this.processSliceCollection(slices);
    this.slices = processed;
    this.slicesVertical = vertical;
    
    if (!this.hasCircleScans) 
      this.radialSlices = this.slices;
    else {
      this.radialSlices = this.slices.slice(0, this.slices.length - 3);
      this.circleSlices = this.slices.slice(this.slices.length - 3);
    }
    this.direction = this.circleSlices.length > 0 && this.circleSlices[0].direction != "" ? this.circleSlices[0].direction : "CW";
    this.signalReady();
  }

  processSliceCollection(slices) {
    if (!slices || !slices.length) return { processed: [] };
    let vertical = slices[0].vertical;
    let exStart = 0.02;
    let exEnd = 0.98;
    let exLength = slices.length > 1 ? (exEnd - exStart) / (slices.length - 1) : 0;
    let processed;
    if (slices.filter(e => "direction" in e && e.direction != "").length == 0) {
      this.hasCircleScans = false;
      processed = slices.map(slice => {
        return {
          index: slice.index,
          imgKey: slice.imgKey,
          sX: slice.startPoint.x,
          sY: slice.startPoint.y,
          eX: slice.endPoint.x,
          eY: slice.endPoint.y
        }
      }).sort((a, b) => {
        if (vertical) return a.eX - b.eX;
        return a.eY - b.eY;
      });
  
      processed.forEach((slice, index) => {
        if (vertical) {
          slice.exStartY = 0.02;
          slice.exEndY = 0.10;
          slice.exStartX = slice.exEndX = exStart + index * exLength;
        } else {
          slice.exStartX = 0.02;
          slice.exEndX = 0.10;
          slice.exStartY = slice.exEndY = exStart + index * exLength;
        }
      });
    }
    else {
      this.hasCircleScans = true;
      let radialSlices = slices.slice(0, slices.length - 3);
      let circleSlices = slices.slice(slices.length - 3);

      let radialProcessed = radialSlices.map(slice => {
        return {
          index: slice.index,
          imgKey: slice.imgKey,
          sX: slice.startPoint.x,
          sY: slice.startPoint.y,
          eX: slice.endPoint.x,
          eY: slice.endPoint.y
        }
      }).sort((a, b) => {
        if (vertical) return a.eX - b.eX;
        return a.eY - b.eY;
      });
  
      radialProcessed.forEach((slice, index) => {
        if (vertical) {
          slice.exStartY = 0.02;
          slice.exEndY = 0.10;
          slice.exStartX = slice.exEndX = exStart + index * exLength;
        } else {
          slice.exStartX = 0.02;
          slice.exEndX = 0.10;
          slice.exStartY = slice.exEndY = exStart + index * exLength;
        }
      });
  
      let circleProcessed = circleSlices.map(slice => {
        return {
          index: slice.index,
          imgKey: slice.imgKey,
          sX: slice.startPoint.x,
          sY: slice.startPoint.y,
          eX: slice.endPoint.x,
          eY: slice.endPoint.y,
          direction: slice.direction
        }
      }).sort((a, b) => {
        if (vertical) return a.eX - b.eX;
        return a.eY - b.eY;
      });
  
      processed = radialProcessed.concat(circleProcessed);
    }

    return { processed, vertical };
  }

  updateBounds() {
    this.minX = this.maxX = this.minY = this.maxY = 0.5;
    if (this.sliceBounds) this.applyBounds(this.sliceBounds);
  }

  applyBounds(bounds) {
    this.maxX = Math.max(this.maxX, bounds.maxX);
    this.minX = Math.min(this.minX, bounds.minX);
    this.maxY = Math.max(this.maxY, bounds.maxY);
    this.minY = Math.min(this.minY, bounds.minY);
  }

  updateSliceBounds(bounds) {
    this.sliceBounds = bounds;
    this.updateBounds();
    this.signalReady();
  }

  updatesliceCurrent(slice) {
    this.sliceCurrent = slice ? slice.index : null;
    this.signalReady();
  }

  getHoveredSlices(x, y, isDragging, outOfBoundsDirections) {
    return {
      slice: isDragging && outOfBoundsDirections == Directions.Vertical ? null : this.sliceAt(this.slices, this.slicesVertical, x, y),
      composite: isDragging && outOfBoundsDirections == Directions.Horizontal ? null :this.sliceAt(this.composites, this.compositesVertical, x, y)
    };
  }

  sliceAt(slices, vertical, x, y) {
    if (!slices.length) return null;

    let sorted = [...slices];
    sorted.sort((a, b) => this.getDistanceFromSlice(a, x, y) - this.getDistanceFromSlice(b, x, y));
    return sorted[0].index;
  }

  getDistanceFromSlice(slice, x, y) {
    if (slice.index < this.slices.length - 3 || !this.hasCircleScans) {
      let dy = slice.eY - slice.sY;
      let dx = slice.eX - slice.sX;

      let numerator = Math.abs(dy * x - dx * y
        + slice.eX * slice.sY - slice.eY * slice.sX);
      let denominator = Math.sqrt(dy * dy + dx * dx);
      return numerator / denominator;
    }
    else
      return this.getDistanceFromCircleSlice(slice, x, y);
  }

  getDistanceFromCircleSlice(slice, x, y) {
    let angle = Math.atan2(y - slice.eY, x - slice.eX);
    let pos = getArcPosition(slice.eX, slice.eY, (slice.eX - slice.sX), (slice.eX - slice.sX), angle);

    let distance = Math.sqrt((x - pos.x) * (x - pos.x) + (y - pos.y) * (y - pos.y))
  
    return distance;
  }

  updateDrawAllSlices(drawAllSlices) {
    this.drawAllSlices = drawAllSlices;
    this.signalReady();
  }
  
  updateShowBindingBox(showBindingBox) {
    this.showBindingBox = showBindingBox;
    this.signalReady();
  }
}

export default RadialCirclesPattern;